import { AuthContext } from '@/contexts/AuthContext';
import { useCallback, useContext } from 'react';
import { TeamCreateRequest, TeamUpdateRequest } from 'callabo-api/src';
import { CallaboApiClient } from 'callabo-api/src/CallaboApiClient';
import { getTeamState, setTeamState, TeamModel } from './TeamStore';
import { WorkspaceModel } from 'libs/callabo-state/models/WorkspaceModel';

export function useFetchTeams() {
  const { getAuthState } = useContext(AuthContext);

  return useCallback(async (callaboApiClient: CallaboApiClient, workspace: WorkspaceModel) => {
    const authState = getAuthState();
    if (authState.type !== 'signedIn') return;
    if (!callaboApiClient || !workspace) return;

    setTeamState({ type: 'loading' });
    try {
      const teams = await callaboApiClient.team.listTeamV1WorkspacesSlugTeamsGet(workspace.id);
      setTeamState({
        type: 'loaded',
        detail: teams,
      });
    } catch (e) {
      setTeamState({ type: 'error', error: e });
      throw e;
    }
  }, []);
}

/** @description team 생성 */
export function useCreateTeam() {
  const { getAuthState } = useContext(AuthContext);

  return useCallback(async (workspaceId: string, data: TeamCreateRequest): Promise<void> => {
    const authState = getAuthState();
    const prevTeamState = getTeamState();
    if (authState.type !== 'signedIn') return;

    const { callaboApiClient } = authState;
    const newTeam = await callaboApiClient.team.createTeamV1WorkspacesSlugTeamsPost(workspaceId, {
      ...data,
      email: data.email || null, // 명시적 삭제를 위해 null 값 전달
    });
    if (prevTeamState.type === 'loaded') {
      setTeamState({
        type: 'loaded',
        detail: [...prevTeamState.detail, newTeam],
      });
    }
  }, []);
}

/** @description team 정보 변경 */
export function useUpdateTeam() {
  const { getAuthState } = useContext(AuthContext);

  return useCallback(
    async (workspaceId: string, teamId: number, data: TeamUpdateRequest): Promise<TeamModel> => {
      const authState = getAuthState();
      const prevTeamDetailState = getTeamState();
      const prevTeamState = getTeamState();
      if (authState.type !== 'signedIn' || prevTeamDetailState.type !== 'loaded') return;

      const { callaboApiClient } = authState;
      const _updatedTeam = await callaboApiClient.team.updateTeamV1WorkspacesSlugTeamsIdPatch(
        workspaceId,
        teamId,
        {
          ...data,
          email: data.email || null, // 명시적 삭제를 위해 null 값 전달
        }
      );

      const updatedTeamsDetail = prevTeamDetailState.detail.map((team) => {
        if (team.id === teamId) {
          return {
            ...team,
            name: _updatedTeam.name,
            email: _updatedTeam.email,
            members: _updatedTeam.team_members?.map((member) => member.member_id),
          };
        }
        return team;
      });
      setTeamState({
        type: 'loaded',
        detail: updatedTeamsDetail,
      });
      if (prevTeamState.type === 'loaded') {
        const updatedTeams = prevTeamState.detail.map((team) => {
          if (team.id === teamId) {
            return {
              ...team,
              name: _updatedTeam.name,
              email: _updatedTeam.email,
            };
          }
          return team;
        });
        setTeamState({
          type: 'loaded',
          detail: updatedTeams,
        });
      }

      return updatedTeamsDetail.find((team) => team.id === teamId);
    },
    []
  );
}

/** @description team 삭제 */
export function useDeleteTeam() {
  const { getAuthState } = useContext(AuthContext);

  return useCallback(async (workspaceId: string, teamId: number): Promise<void> => {
    const authState = getAuthState();
    const prevTeamState = getTeamState();
    if (authState.type !== 'signedIn' || prevTeamState.type !== 'loaded') return;

    const { callaboApiClient } = authState;
    await callaboApiClient.team.deleteTeamV1WorkspacesSlugTeamsIdDelete(workspaceId, teamId);

    setTeamState({
      type: 'loaded',
      detail: prevTeamState.detail.filter((team) => team.id !== teamId),
    });
  }, []);
}
